import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Search, X } from 'lucide-react';
import { getCaseStudies, CASE_STUDY_CATEGORIES, CASE_STUDY_FILTER_KEY } from '@/data/caseStudies';
import { useLiveTick } from '@/lib/liveContent';
import { Reveal } from '../Reveal';
import { cn } from '@/lib/utils';

const CategoryChip = ({ categoryKey }) => {
  const cat = CASE_STUDY_CATEGORIES[categoryKey];
  return (
    <span
      className="rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide"
      style={{ backgroundColor: cat.soft, color: cat.dark }}
    >
      {cat.label}
    </span>
  );
};

const CaseStudyCard = ({ cs, index }) => (
  <Reveal delay={(index % 3) * 0.07} className="h-full">
    <Link
      to={`/case-studies/${cs.slug}`}
      data-testid={`case-study-card-${cs.slug}`}
      aria-label={`View case study: ${cs.title}`}
      className="group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-lift"
    >
      {cs.image && (
        <div className="overflow-hidden">
          <img
            src={cs.image.src}
            alt={cs.image.alt}
            width="1080"
            height="675"
            loading="lazy"
            className="aspect-[16/10] w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      )}
      <div className="flex flex-1 flex-col p-6">
        <div className="flex flex-wrap items-center gap-2">
          {cs.categories.map((key) => (
            <CategoryChip key={key} categoryKey={key} />
          ))}
          {cs.industry && <span className="text-xs font-medium text-slate-500">{cs.industry}</span>}
        </div>
        <p className="mt-4 text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{cs.client}</p>
        <h3 className="mt-1.5 text-lg font-extrabold leading-snug tracking-tight text-navy-900 transition-colors group-hover:text-navy-600">
          {cs.title}
        </h3>
        <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{cs.summary}</p>
        {cs.outcomes.length > 0 && (
          <ul className="mt-4 space-y-2">
            {cs.outcomes.slice(0, 2).map((outcome) => (
              <li key={outcome} className="flex items-start gap-2 text-sm font-medium text-navy-800">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold-700" aria-hidden="true" />
                {outcome}
              </li>
            ))}
          </ul>
        )}
        <span className="mt-auto inline-flex items-center gap-1.5 pt-5 text-sm font-bold text-navy-900">
          View Case Study
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
        </span>
      </div>
    </Link>
  </Reveal>
);

const matchesQuery = (cs, q) => {
  const haystack = [cs.client, cs.title, cs.industry, cs.challenge, cs.summary, ...cs.services]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return q.split(/\s+/).every((term) => haystack.includes(term));
};

export const CaseStudyExplorer = () => {
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');

  const tick = useLiveTick();
  const published = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => getCaseStudies().filter((cs) => cs.status === 'published').sort((a, b) => a.order - b.order),
    [tick],
    []
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return published.filter((cs) => {
      const categoryMatch = category === 'all' || cs.categories.includes(category);
      const queryMatch = !q || matchesQuery(cs, q);
      return categoryMatch && queryMatch;
    });
  }, [published, category, query]);

  useEffect(() => {
    if (category === 'all') window.sessionStorage.removeItem(CASE_STUDY_FILTER_KEY);
    else window.sessionStorage.setItem(CASE_STUDY_FILTER_KEY, category);
  }, [category]);

  const isFiltered = category !== 'all' || query.trim() !== '';

  const clearFilters = () => {
    setCategory('all');
    setQuery('');
  };

  const filterOptions = [
    { key: 'all', label: 'All Case Studies' },
    ...Object.entries(CASE_STUDY_CATEGORIES).map(([key, cat]) => ({ key, label: cat.label })),
  ];

  return (
    <section id="case-study-results" aria-labelledby="explorer-heading" data-testid="case-study-explorer" className="bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl" data-testid="explorer-header">
          <p className="flex items-center gap-3">
            <span className="font-mono text-xs font-medium tracking-widest text-gold-700">02</span>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-700">Browse the work</span>
          </p>
          <h2 id="explorer-heading" className="mt-4 text-balance text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl">
            Explore case studies by service
          </h2>
        </div>

        <div className="mt-10 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filter case studies by service">
            {filterOptions.map((option) => {
              const count =
                option.key === 'all'
                  ? published.length
                  : published.filter((cs) => cs.categories.includes(option.key)).length;
              const isActive = category === option.key;
              return (
                <button
                  key={option.key}
                  type="button"
                  aria-pressed={isActive}
                  data-testid={`filter-${option.key}`}
                  onClick={() => setCategory(option.key)}
                  className={cn(
                    'rounded-full border-2 px-4 py-2 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-2',
                    isActive
                      ? 'border-navy-900 bg-navy-900 text-white'
                      : 'border-slate-200 bg-white text-navy-800 hover:border-navy-300 hover:bg-navy-50'
                  )}
                >
                  {option.label}
                  <span className={cn('ml-1.5 text-xs font-semibold', isActive ? 'text-slate-300' : 'text-slate-400')}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="relative w-full lg:max-w-xs">
            <label htmlFor="case-study-search" className="sr-only">
              Search case studies
            </label>
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input
              id="case-study-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search client, project or service…"
              data-testid="case-study-search"
              className="h-11 w-full rounded-md border border-slate-300 bg-white pl-10 pr-9 text-sm text-navy-900 shadow-sm transition-colors placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-1"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear search"
                data-testid="case-study-search-clear"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 transition-colors hover:text-navy-900"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-4">
          <p aria-live="polite" className="text-sm font-semibold text-slate-600" data-testid="results-count">
            Showing {filtered.length} of {published.length} case studies
          </p>
          {isFiltered && (
            <button
              type="button"
              onClick={clearFilters}
              data-testid="clear-filters-btn"
              className="inline-flex items-center gap-1.5 text-sm font-bold text-gold-700 underline-offset-2 transition-colors hover:text-gold-700 hover:underline"
            >
              <X className="h-4 w-4" aria-hidden="true" />
              Clear Filters
            </button>
          )}
        </div>

        {filtered.length > 0 ? (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="case-study-grid">
            {filtered.map((cs, i) => (
              <CaseStudyCard key={cs.id} cs={cs} index={i} />
            ))}
          </div>
        ) : (
          <div
            className="mt-8 rounded-2xl border-2 border-dashed border-slate-300 bg-mist px-6 py-14 text-center"
            data-testid="case-study-empty"
          >
            <p className="text-lg font-extrabold text-navy-900">No matching case studies</p>
            <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-500">
              Try a different service category or search term — or clear the filters to see all of our work.
            </p>
            <button
              type="button"
              onClick={clearFilters}
              data-testid="empty-clear-filters-btn"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-navy-900 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-navy-700"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
